import { useRef, useEffect, useState } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { Button } from '../../../../../components/ui/button';
import { Card } from '../../../../../components/ui/card';
import { Input } from '../../../../../components/ui/input';
import { Badge } from '../../../../../components/ui/badge';
import { motion } from 'framer-motion';
import { useExpandable } from '../../../../../hooks/use-expandable';
import { useImageUpload } from '../../../../../hooks/use-image-upload';
import { FileText, ChevronDown, ChevronUp, Upload, Trash2, AlertCircle, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import { cn } from '../../../../../lib/utils';
import { storageService } from '../../../services/storageService';
import { messagesApi } from '../../../services/messagesApi';
import { toast } from '@/hooks/use-toast';
import { AddNodeMenu } from './AddNodeMenu';

interface DocumentMessageData {
  label?: string;
  documentUrl?: string;
  caption?: string;
  messageId?: string;
  processingStatus?: 'pending' | 'processing' | 'completed' | 'error';
  flowId?: string;
  groupId?: string;
  sequenceOrder?: number;
}

export function DocumentMessageNode({ id, data, isConnectable }: NodeProps<DocumentMessageData>) {
  const contentRef = useRef<HTMLDivElement>(null);
  const { isExpanded, toggleExpand, animatedHeight } = useExpandable(false);
  const { setNodes, getNodes } = useReactFlow();
  const [caption, setCaption] = useState(data.caption || '');
  const [hasQuotes, setHasQuotes] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<'pending' | 'processing' | 'completed' | 'error'>(
    data.processingStatus || 'pending'
  );

  const {
    previewUrl,
    fileName,
    fileInputRef,
    handleThumbnailClick,
    handleFileChange,
    handleRemove,
  } = useImageUpload({
    onUpload: (url) => console.log("Documento cargado:", url),
    initialUrl: data.documentUrl,
    initialFileName: data.caption || data.documentUrl?.split('/').pop() || null,
  });

  useEffect(() => {
    if (contentRef.current) {
      animatedHeight.set(isExpanded ? contentRef.current.offsetHeight : 0);
    }
  }, [isExpanded, animatedHeight, previewUrl]);

  const handleCaptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newCaption = e.target.value;
    setCaption(newCaption);

    const hasQuotesDetected = /["']/.test(newCaption);
    setHasQuotes(hasQuotesDetected);
  };

  const getFileExtension = (filename: string) => {
    return filename.split('.').pop()?.toUpperCase() || 'DOC';
  };

  const handleSave = async () => {
    // Permitir actualizar si ya existe un messageId y documentUrl
    if (!previewUrl && !data.documentUrl) {
      toast.error('Debes seleccionar un documento primero');
      return;
    }

    if (hasQuotes) {
      toast.error('Las comillas no están permitidas en el caption');
      return;
    }

    setIsSaving(true);
    setProcessingStatus('processing');

    try {
      // Obtener valores actualizados del nodo
      const currentNodes = getNodes();
      const currentNode = currentNodes.find(n => n.id === id);

      if (!currentNode) {
        throw new Error('Nodo no encontrado');
      }

      let finalDocumentUrl = data.documentUrl;
      let finalFileName = fileName;

      // 1. Solo subir si es un nuevo documento (blob URL) o si no hay documentUrl previa
      if (previewUrl && previewUrl.startsWith('blob:') && fileInputRef.current?.files?.[0]) {
        const file = fileInputRef.current.files[0];
        const uploadResult = await storageService.uploadFile(file, 'document');
        finalDocumentUrl = uploadResult.url;
        finalFileName = uploadResult.fileName;
      }

      // 2. Guardar en BD
      const savedMessage = await messagesApi.upsertMessage({
        id: data.messageId,
        node_id: id,
        node_type: 'document',
        text_content: caption,
        media_url: finalDocumentUrl!,
        file_name: finalFileName!,
        ai_processing_status: 'completed',
        mime_type: fileInputRef.current?.files?.[0]?.type || (finalFileName ? `application/${finalFileName.split('.').pop()?.toLowerCase()}` : 'application/octet-stream'),
        file_size: fileInputRef.current?.files?.[0]?.size || 0,
        flow_id: currentNode.data.flowId || null,
        group_id: currentNode.data.groupId || null,
        sequence_order: currentNode.data.sequenceOrder || 1,
      });

      // 3. Actualizar el nodo en ReactFlow
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? {
              ...node,
              data: {
                ...node.data,
                documentUrl: finalDocumentUrl,
                caption: caption,
                messageId: savedMessage.id,
                processingStatus: 'completed'
              }
            }
            : node
        )
      );

      setProcessingStatus('completed');
      toast.success(data.messageId ? 'Documento actualizado' : 'Documento guardado correctamente');
    } catch (error) {
      console.error('Error:', error);
      setProcessingStatus('error');
      toast.error('Error al guardar el documento');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!data.messageId) {
      toast.error('No hay mensaje guardado para eliminar');
      return;
    }

    if (!window.confirm('¿Estás seguro de eliminar este documento de la base de datos?')) {
      return;
    }

    try {
      await messagesApi.deleteMessage(id);

      // Actualizar el nodo en ReactFlow
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? {
              ...node,
              data: {
                ...node.data,
                messageId: undefined,
                processingStatus: 'pending'
              }
            }
            : node
        )
      );

      toast.success('Documento eliminado de la base de datos');
    } catch (error) {
      console.error('Error eliminando mensaje:', error);
      toast.error('Error al eliminar el documento');
    }
  };

  const getStatusBadge = () => {
    if (isSaving) {
      return (
        <Badge className="bg-blue-500">
          <Loader2 className="h-3 w-3 animate-spin" />
        </Badge>
      );
    }
    if (processingStatus === 'completed' && data.messageId) {
      return (
        <Badge className="bg-green-500">
          <CheckCircle className="h-3 w-3" />
        </Badge>
      );
    }
    if (processingStatus === 'error') {
      return (
        <Badge className="bg-red-500">
          <AlertTriangle className="h-3 w-3" />
        </Badge>
      );
    }
    return (
      <Badge className="bg-yellow-500">
        <AlertCircle className="h-3 w-3" />
      </Badge>
    );
  };

  return (
    <Card className="min-w-[300px] max-w-[400px] bg-white shadow-lg border-2 border-indigo-200">
      {/* Número de secuencia - arriba izquierda */}
      {data.groupId && (
        <div className="absolute -top-3 -left-3 z-10">
          <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-bold shadow-lg border-2 border-white">
            {data.sequenceOrder || 1}
          </div>
        </div>
      )}

      <div className="absolute -top-2 -right-2 z-10">
        {getStatusBadge()}
      </div>

      <Handle
        type="target"
        position={Position.Left}
        isConnectable={isConnectable}
        className="w-5 h-5 !bg-indigo-500 hover:w-6 hover:h-6 transition-all cursor-crosshair"
        style={{ border: '3px solid white' }}
      />

      <div className="p-4">
        <div className="flex justify-between items-center gap-2 mb-2">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-indigo-500" />
            <h3 className="font-medium text-foreground">Enviar Documento</h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleExpand}
            className="h-8 w-8 p-0"
          >
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>

        <Input
          type="file"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
          className="hidden"
          ref={fileInputRef}
          onChange={handleFileChange}
        />

        <motion.div
          style={{ height: animatedHeight }}
          className="overflow-hidden"
        >
          <div ref={contentRef} className="space-y-3 pt-2">
            {!previewUrl ? (
              <div
                onClick={handleThumbnailClick}
                className={cn(
                  "flex h-32 cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-indigo-300 bg-indigo-50 transition-colors hover:bg-indigo-100"
                )}
              >
                <div className="rounded-full bg-white p-3 shadow-sm">
                  <FileText className="h-6 w-6 text-indigo-400" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-700">Seleccionar documento</p>
                  <p className="text-xs text-gray-500">PDF, DOC, XLS, TXT, CSV</p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4">
                  <div className="flex items-center gap-3">
                    <div className="rounded-lg bg-indigo-100 p-3 min-w-[60px] text-center">
                      <FileText className="h-6 w-6 text-indigo-500 mx-auto mb-1" />
                      <span className="text-xs font-bold text-indigo-700">
                        {fileName ? getFileExtension(fileName) : 'DOC'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {fileName}
                      </p>
                      <p className="text-xs text-gray-500">Documento</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={handleThumbnailClick}
                        className="h-8 w-8 p-0"
                      >
                        <Upload className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={handleRemove}
                        className="h-8 w-8 p-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="text-xs text-gray-600 mb-1 block">
                Mensaje o Caption (opcional):
              </label>
              <textarea
                value={caption}
                onChange={handleCaptionChange}
                placeholder="Texto que acompañará el documento..."
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 resize-none ${hasQuotes
                  ? 'border-red-500 focus:ring-red-500 bg-red-50'
                  : 'border-gray-300 focus:ring-primary'
                  }`}
                rows={3}
              />

              {hasQuotes && (
                <div className="flex items-start gap-2 mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700">
                    Las comillas no están permitidas en los flujos de mensaje.
                  </p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>{caption.length} caracteres</span>
              <div className="flex items-center gap-2">
                {data.messageId && (
                  <Button
                    size="sm"
                    variant="destructive"
                    className="h-7 w-7 p-0"
                    onClick={handleDelete}
                    disabled={isSaving}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
                <Button
                  size="sm"
                  className="h-7 text-xs"
                  disabled={!previewUrl || hasQuotes || isSaving}
                  onClick={handleSave}
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    data.messageId ? 'Actualizar' : 'Guardar'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>

        {!isExpanded && previewUrl && (
          <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
            <FileText className="h-3 w-3" />
            <span className="truncate">{fileName || 'Documento cargado'}</span>
          </div>
        )}
      </div>

      <Handle
        type="source"
        position={Position.Right}
        isConnectable={isConnectable}
        className="w-5 h-5 !bg-indigo-500 hover:w-6 hover:h-6 transition-all cursor-crosshair"
        style={{ border: '3px solid white' }}
      />
      <AddNodeMenu nodeId={id} />
    </Card>
  );
}
